// The one sign-in session, from kast-common's auth: tokens from
// login.kast-dev.net under the family's auth_* keys, refreshed before expiry
// and on a 401, revoked on sign-out. What stays here is Salvagers' own: its
// Cognito app client, its admin groups and where sign-in and sign-out go.

import {
  createAdminMode,
  createSession,
  hostedUiLogoutUrl,
  localStorageTokenStore,
  redirectToSignIn,
  takeTokensFromUrl,
} from "@jkast90/kast-common/auth";
import { getDeploymentConfig } from "@common/deployment-config";

/**
 * Groups granting admin rights; must match AdminUser in api/src/auth.rs,
 * which ignores case, so the UI's checks pass `ignoreCase` too.
 * `admin` is the shared kast-dev group, `salvagers-admin` this app's. Client
 * checks only decide what to show: the API re-checks on every admin route.
 */
export const ADMIN_GROUPS = ["admin", "salvagers-admin"] as const;

/**
 * Admin UI is opt-in, as in waypoint_wars: the admin group only offers the
 * "Admin tools" toggle on the account page, and it starts off. The saved
 * choice never grants on its own, so losing the group drops the tools. One
 * switch for the whole app, under the key it has always had, so an admin's
 * choice survives. Read it with `useAdminMode(adminMode, user)`.
 */
export const adminMode = createAdminMode({ admin: ADMIN_GROUPS, ignoreCase: true, storageKey: "salvagers_admin_mode" });

const { cognitoDomain, clientId, loginUrl } = getDeploymentConfig();

export const session = createSession({ cognitoDomain, clientId, store: localStorageTokenStore() });

// Back from login.kast-dev.net: take the hash's tokens before the first
// render, so the first frame is already signed in.
const taken = takeTokensFromUrl(session);
if (taken?.returnTo) window.history.replaceState(null, "", taken.returnTo);

/** To login.kast-dev.net, and back to this page (path and query). */
export const signIn = () => void redirectToSignIn({ signInUrl: `${loginUrl}/signin` });

/** Revokes the refresh token, clears the session, then ends the hosted UI's session too when configured. */
export const signOut = () =>
  session.signOut({
    leave:
      cognitoDomain && clientId
        ? () => window.location.assign(hostedUiLogoutUrl({ cognitoDomain, clientId, logoutUri: window.location.origin }))
        : undefined,
  });
